import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Alert, api, Badge, Button, fmtDate, Input, ListPage, Modal, useAuth, usePagedList } from '@greenrinse/ui';

export interface DisputeDto {
  id: string; periodId: string; periodName: string; matchResultId: string | null; washRecordId: string | null; plate: string | null; date: string | null; category: string | null;
  status: 'Open' | 'UnderReview' | 'Accepted' | 'Rejected' | 'Withdrawn'; reason: string; openedByName: string; openedByPartner: boolean; openedAtUtc: string;
  resolvedByName: string | null; resolvedAtUtc: string | null; resolutionNote: string | null; afterLock: boolean;
  messages: { id: string; authorName: string; fromPartner: boolean; body: string; atUtc: string }[];
}

export function disputeTone(s: string): 'default' | 'success' | 'warning' | 'error' | 'accent' {
  return s === 'Accepted' ? 'success' : s === 'Rejected' ? 'error' : s === 'Withdrawn' ? 'default' : s === 'UnderReview' ? 'accent' : 'warning';
}

/** Conversation + resolution. Used by both the admin app and (read/reply only) the partner portal. */
export function DisputeThread({ dispute, onChanged, partnerMode = false }: { dispute: DisputeDto; onChanged: () => void; partnerMode?: boolean }) {
  const { user } = useAuth();
  const canResolve = !partnerMode && ['SuperAdmin', 'RegionalManager', 'Finance'].includes(user?.role ?? '');
  const [d, setD] = useState(dispute);
  const [body, setBody] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const closed = d.status === 'Accepted' || d.status === 'Rejected' || d.status === 'Withdrawn';

  const post = async (path: string, payload?: unknown) => {
    setBusy(true); setError(null);
    try { setD(await api<DisputeDto>(`/api/disputes/${d.id}/${path}`, { method: 'POST', body: payload ?? {} })); setBody(''); onChanged(); }
    catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

  return (
    <div className="gr-stack">
      <div className="gr-row" style={{ justifyContent: 'space-between' }}>
        <div><strong>{d.plate ?? '—'}</strong> <span className="gr-muted">{d.date ?? ''} · {d.category ?? ''} · {d.periodName}</span></div>
        <Badge tone={disputeTone(d.status)}>{d.status}{d.afterLock ? ' · after lock' : ''}</Badge>
      </div>
      {error && <Alert tone="error">{error}</Alert>}
      <div className="gr-stack" style={{ gap: 6, maxHeight: 320, overflow: 'auto' }}>
        {d.messages.map((m) => (
          <div key={m.id} className="gr-card gr-card--sm" style={{ padding: '8px 12px', background: m.fromPartner ? 'var(--gr-surface-2)' : 'var(--gr-primary-container)', alignSelf: m.fromPartner ? 'flex-start' : 'flex-end', maxWidth: '85%' }}>
            <div className="gr-muted" style={{ fontSize: 12 }}>{m.authorName}{m.fromPartner ? ' (partner)' : ''} · {fmtDate(m.atUtc)}</div>
            <div style={{ whiteSpace: 'pre-wrap' }}>{m.body}</div>
          </div>
        ))}
      </div>
      {d.resolutionNote && <Alert tone={d.status === 'Accepted' ? 'success' : 'info'}>{d.status} by {d.resolvedByName} on {fmtDate(d.resolvedAtUtc)}: {d.resolutionNote}</Alert>}
      {!closed && (
        <>
          <div className="gr-row">
            <input className="gr-input" style={{ flex: 1 }} placeholder="Reply…" value={body} onChange={(e) => setBody(e.target.value)} />
            <Button variant="tonal" loading={busy} disabled={!body.trim()} onClick={() => void post('messages', { body })}>Send</Button>
          </div>
          {canResolve && (
            <div className="gr-card gr-card--tonal gr-card--sm">
              <Input label="Resolution note" value={note} onChange={(e) => setNote(e.target.value)} />
              <div className="gr-row">
                <Button loading={busy} disabled={!note.trim()} onClick={() => void post('resolve', { accept: true, note })}>Accept dispute</Button>
                <Button variant="danger" loading={busy} disabled={!note.trim()} onClick={() => void post('resolve', { accept: false, note })}>Reject dispute</Button>
                <span className="gr-muted" style={{ fontSize: 12 }}>Accepting makes the disputed wash non-billable{d.afterLock ? ' (credit note in billing)' : ''}.</span>
              </div>
            </div>
          )}
          {(partnerMode || d.openedByName === user?.fullName) && <Button variant="ghost" onClick={() => void post('withdraw')}>Withdraw</Button>}
        </>
      )}
    </div>
  );
}

export default function DisputesPage() {
  const [status, setStatus] = useState('');
  const list = usePagedList<DisputeDto>('/api/disputes', { status: status || undefined });
  const [open, setOpen] = useState<DisputeDto | null>(null);
  const [params] = useSearchParams();
  const deepLink = params.get('open');
  useEffect(() => { if (deepLink) api<DisputeDto>(`/api/disputes/${deepLink}`).then(setOpen).catch(() => undefined); }, [deepLink]);
  return (
    <>
      <ListPage<DisputeDto>
        title="Disputes" state={list} rowKey={(d) => d.id} onRowClick={(d) => setOpen(d)}
        filters={<select className="gr-select" style={{ width: 'auto', minHeight: 40 }} value={status} onChange={(e) => setStatus(e.target.value)}><option value="">All</option>{['Open', 'UnderReview', 'Accepted', 'Rejected', 'Withdrawn'].map((s) => <option key={s}>{s}</option>)}</select>}
        columns={[
          { key: 'plate', header: 'Item', render: (d) => <><strong>{d.plate ?? '—'}</strong><div className="gr-muted" style={{ fontSize: 12 }}>{d.date ?? ''} · {d.category ?? ''}</div></> },
          { key: 'period', header: 'Period', render: (d) => d.periodName },
          { key: 'by', header: 'Opened by', render: (d) => <>{d.openedByName}{d.openedByPartner && <Badge tone="accent">partner</Badge>}<div className="gr-muted" style={{ fontSize: 12 }}>{fmtDate(d.openedAtUtc)}</div></> },
          { key: 'reason', header: 'Reason', render: (d) => <span style={{ fontSize: 13 }}>{d.reason.slice(0, 120)}</span> },
          { key: 'msgs', header: '', render: (d) => <span className="gr-muted">{d.messages.length} msg</span> },
          { key: 'status', header: 'Status', render: (d) => <Badge tone={disputeTone(d.status)}>{d.status}</Badge> },
        ]}
      />
      <Modal open={open !== null} title="Dispute" onClose={() => setOpen(null)}>{open && <DisputeThread dispute={open} onChanged={() => list.reload()} />}</Modal>
    </>
  );
}
